import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, QrCode, Download, RefreshCw } from 'lucide-react';
import AdminNavbar from '../../components/layout/AdminNavbar';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const QRCodePage = () => {
    const [qrSecret, setQrSecret] = useState('');
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const printRef = useRef(null);

    useEffect(() => {
        document.title = "QR Code | Savrion Technologies";
        const fetchQrSecret = async () => {
            try {
                const { data } = await api.get('/api/admin/settings/qr-secret');
                setQrSecret(data.secret);
            } catch (err) {
                toast.error('Failed to load QR secret');
            }
        };
        fetchQrSecret();
    }, []);

    const handleGenerateNew = async () => {
        if (!window.confirm('Are you sure you want to generate a new permanent QR code? All previous printed QR codes will stop working instantly.')) {
            return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post('/api/admin/settings/qr-secret/generate');
            setQrSecret(data.secret);
            toast.success('New permanent QR code generated successfully!');
        } catch (err) {
            toast.error('Failed to generate new QR secret');
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = () => {
        const svgElement = printRef.current.querySelector('svg');
        if (!svgElement) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const svgData = new XMLSerializer().serializeToString(svgElement);
        
        const padding = 32; // Add some whitespace around the QR code
        const size = 512;
        canvas.width = size + (padding * 2);
        canvas.height = size + (padding * 2);
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, padding, padding, size, size);
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = 'Aviatricks_Office_QR.png';
            downloadLink.href = pngFile;
            downloadLink.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const windowPrint = window.open('', '', 'width=800,height=600');
        windowPrint.document.write(`
            <html>
                <head>
                    <title>Print Office QR Code</title>
                    <style>
                        body {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            height: 100vh;
                            margin: 0;
                            font-family: system-ui, -apple-system, sans-serif;
                            text-align: center;
                        }
                        h1 { font-size: 32px; margin-bottom: 8px; }
                        p { font-size: 18px; color: #555; margin-bottom: 32px; }
                        .qr-container { padding: 32px; border: 4px solid #000; border-radius: 24px; }
                        @media print {
                            body { -webkit-print-color-adjust: exact; }
                        }
                    </style>
                </head>
                <body>
                    <h1>Aviatricks Office Attendance</h1>
                    <p>Scan this QR code using the Employee Dashboard to check in or out.</p>
                    <div class="qr-container">
                        ${printContent.innerHTML}
                    </div>
                </body>
            </html>
        `);
        windowPrint.document.close();
        windowPrint.focus();
        setTimeout(() => {
            windowPrint.print();
            windowPrint.close();
        }, 250);
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 w-full p-4 md:p-8 overflow-x-hidden">
                <div className="max-w-5xl mx-auto animate-fade-in">
                    
                    <div className="mb-8">
                        <p className="text-xs font-black uppercase tracking-widest text-[var(--accent-primary)] mb-1">
                            Access Control
                        </p>
                        <h1 className="text-3xl font-black tracking-tight">Office QR Code</h1>
                    </div>

                    <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[2.5rem] p-8 md:p-12 shadow-[var(--shadow-soft)]">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            
                            {/* Left Side: QR Code Display */}
                            <div className="flex flex-col items-center justify-center">
                                <div ref={printRef} className="bg-[var(--card-bg)] p-6 rounded-[2rem] border border-slate-200 shadow-sm transition-transform duration-500 hover:scale-[1.02]">
                                    <QRCodeSVG
                                        value={qrSecret || 'AVIATRICKS_PERMANENT_QR_2024'}
                                        size={280}
                                        level="H"
                                        includeMargin={true}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                        className="rounded-xl"
                                    />
                                </div>
                                <div className="mt-6 flex items-center gap-2 text-sm font-bold text-[var(--text-secondary)]">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                    Active Permanent QR
                                </div>
                            </div>

                            {/* Right Side: Information & Actions */}
                            <div className="flex flex-col space-y-8">
                                <div>
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] mb-6">
                                        <QrCode size={24} />
                                    </div>
                                    <h2 className="text-2xl font-black mb-3">Deploy your code</h2>
                                    <p className="text-base text-[var(--text-secondary)] leading-relaxed">
                                        Print or download this unique QR code and place it at your office entrance. Employees will scan this code using their mobile dashboard to instantly log their check-ins and check-outs.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        onClick={handleDownload}
                                        className="flex items-center justify-center gap-2 w-full bg-[var(--text-primary)] hover:bg-slate-700 text-[var(--bg-primary)] font-bold py-3.5 px-6 rounded-2xl transition-all active:scale-95 shadow-md"
                                    >
                                        <Download size={18} />
                                        Download PNG
                                    </button>

                                    <button
                                        onClick={handlePrint}
                                        className="flex items-center justify-center gap-2 w-full bg-transparent hover:bg-[var(--bg-secondary)] text-[var(--text-primary)] border-2 border-[var(--border-color)] font-bold py-3.5 px-6 rounded-2xl transition-all active:scale-95"
                                    >
                                        <Printer size={18} />
                                        Print Document
                                    </button>
                                </div>

                                <div className="pt-8 border-t border-[var(--border-color)]">
                                    <h3 className="text-sm font-bold text-red-500 mb-2 uppercase tracking-widest">Security Action</h3>
                                    <p className="text-sm text-[var(--text-secondary)] mb-4">
                                        If your QR code is compromised, you can generate a new one. This will immediately invalidate all previously printed QR codes.
                                    </p>
                                    <button
                                        onClick={handleGenerateNew}
                                        disabled={generating}
                                        className="inline-flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 font-bold py-2.5 px-5 rounded-xl transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        <RefreshCw size={16} className={generating ? "animate-spin" : ""} />
                                        {generating ? 'Rotating Secret...' : 'Rotate QR Secret'}
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default QRCodePage;


